import { useAuth } from './useAuth';
import type { RolePermissions } from '../types/auth';

/**
 * Custom hook for role-based access control
 * Provides easy-to-use permission checking functions
 */
export const useRolePermissions = () => {
  const { user, userRolePermissions, hasPermission, getScope } = useAuth();

  // Check if user is super admin
  const isSuperAdmin = user?.superAdminFlag === 1;

  // Check if user has any role permissions loaded
  const hasRolePermissions = !!userRolePermissions;

  // Permission checking functions for common resources
  const canView = (resource: keyof RolePermissions) => hasPermission(resource as string, 'view');
  const canAdd = (resource: keyof RolePermissions) => hasPermission(resource as string, 'add');
  const canEdit = (resource: keyof RolePermissions) => hasPermission(resource as string, 'edit');
  const canDelete = (resource: keyof RolePermissions) => hasPermission(resource as string, 'delete');
  const canExport = (resource: keyof RolePermissions) => hasPermission(resource as string, 'export');
  const canAssign = (resource: keyof RolePermissions) => hasPermission(resource as string, 'assign');

  // Specific permission checks for common use cases
  const canManageUsers = canView('users') || canAdd('users') || canEdit('users') || canDelete('users') || canAssign('users');
  const canManageNurseries = canView('nurseries') || canAdd('nurseries') || canEdit('nurseries') || canDelete('nurseries');
  const canManageCameras = canView('cameras') || canAdd('cameras') || canEdit('cameras') || canDelete('cameras');
  const canManageChildren = canView('children') || canAdd('children') || canEdit('children') || canDelete('children');
  const canManageTeachers = canView('teachers') || canAdd('teachers') || canEdit('teachers') || canDelete('teachers') || canAssign('teachers');
  const canManageParents = canView('parents') || canAdd('parents') || canEdit('parents') || canDelete('parents') || canAssign('parents');
  const canManageClassrooms = canView('classrooms') || canAdd('classrooms') || canEdit('classrooms') || canDelete('classrooms');
  const canManageSchedules = canView('schedules') || canAdd('schedules') || canEdit('schedules') || canDelete('schedules');
  const canManageEdgeDevices = canView('edgeDevices') || canAdd('edgeDevices') || canEdit('edgeDevices') || canDelete('edgeDevices');
  const canViewReports = canView('reports') || canView('attendance') || canView('childTimeline');
  const canExportReports = canExport('reports');
  const canValidateFaces = canView('validation') || canAdd('validation');
  const canViewDashboard = canView('dashboard');
  const canManageVacations = canView('vacations') || canAdd('vacations') || canEdit('vacations') || canDelete('vacations');
  const canManageHolidays = canView('holidays') || canAdd('holidays') || canEdit('holidays') || canDelete('holidays');
  const canManagePolicies = canView('policy') || canAdd('policy') || canEdit('policy') || canDelete('policy');
  const canViewBilling = canView('billing') || canView('subscriptions') || canView('payouts');
  const canManageBilling = canViewBilling || canAdd('billing') || canEdit('billing') || canDelete('billing') || canAdd('subscriptions') || canEdit('subscriptions') || canDelete('subscriptions') || canAdd('payouts') || canEdit('payouts') || canDelete('payouts');
  const canManageSubscriptions = canView('subscriptions') || canAdd('subscriptions') || canEdit('subscriptions') || canDelete('subscriptions') || canManageBilling;
  const canManageConsent = canView('consent') || canAdd('consent') || canEdit('consent') || canDelete('consent');

  // Custom Reports permissions
  const canViewCustomReports = hasPermission('customReports', 'view') || isSuperAdmin;
  const canCreateCustomReports = hasPermission('customReports', 'create') || isSuperAdmin;
  const canExportCustomReports = hasPermission('customReports', 'export') || isSuperAdmin;
  const canShareCustomReports = hasPermission('customReports', 'share') || isSuperAdmin;
  const canManageCustomReports = hasPermission('customReports', 'manage') || isSuperAdmin;

  // Settings page specific permission checks
  const canViewSettings = isSuperAdmin || hasRolePermissions; // Super admins can always view, others need role permissions
  
  // User management permissions for Settings page
  const canCreateUsers = canAdd('users'); // Can create users in their nursery
  const canEditUsers = canEdit('users'); // Can edit users in their nursery
  const canDeleteUsers = canDelete('users'); // Can delete users in their nursery
  const canViewUsers = canView('users'); // Can view users in their nursery
  const canViewRoles = canView('roles') || canView('users'); // Can view roles if they have roles permission OR users permission
  const canAssignUsers = canAssign('users'); // Can assign users to nurseries
  
  const canManageRoles = canView('roles') || canAdd('roles') || canEdit('roles') || canDelete('roles');
  const canAddRoles = canAdd('roles');
  const canEditRoles = canEdit('roles');
  const canDeleteRoles = canDelete('roles');
  const canViewRolesList = canView('roles');

  // Nursery management permissions for Settings page (super admin only)
  const canViewNurseries = canView('nurseries'); // Can view nurseries
  const canCreateNurseries = canAdd('nurseries'); // Can create nurseries
  const canAccessNurseryManagement = isSuperAdmin; // Only super admins can access Nursery Management
  const canAccessHierarchy = isSuperAdmin; // Only super admins can access Hierarchy Management

  // Snapshot permissions
  const canViewSnapshots = canView('snapshots');
  const canDeleteSnapshots = canDelete('snapshots');
  const canAccessSnapshotSettings = canView('snapshots') || canEdit('snapshots') || isSuperAdmin;
  const canEditSnapshotSettings = canEdit('snapshots') || isSuperAdmin;
  const canAnalyzeSnapshots = hasPermission('snapshots', 'analyze') || isSuperAdmin;
  const canAccessChildImageDisplaySettings = canView('childImageDisplay') || canEdit('childImageDisplay') || isSuperAdmin;
  const canEditChildImageDisplaySettings = canEdit('childImageDisplay') || isSuperAdmin;

  // Settings page access based on permissions
  const canAccessUserManagement = canCreateUsers || canEditUsers || canDeleteUsers || canViewUsers || canAssignUsers;
  const canAccessSettings = canViewSettings && (canAccessUserManagement || canAccessNurseryManagement || canAccessHierarchy || canManageRoles || canAccessChildImageDisplaySettings);

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('🔐 Role Permissions Debug:', {
      userRolePermissions,
      hasRolePermissions,
      canViewSettings,
      canCreateUsers,
      canViewUsers,
      canViewRoles,
      canAssignUsers,
      canViewNurseries,
      canCreateNurseries,
      canAccessUserManagement,
      canAccessNurseryManagement,
      canAccessHierarchy,
      canAccessSettings,
      // Raw permission checks
      usersView: hasPermission('users', 'view'),
      usersAdd: hasPermission('users', 'add'),
      usersAssign: hasPermission('users', 'assign'),
      nurseriesView: hasPermission('nurseries', 'view'),
      nurseriesAdd: hasPermission('nurseries', 'add'),
      rolesView: hasPermission('roles', 'view')
    });
  }

  return {
    canViewEvents: hasPermission('events', 'view'),
    canCreateEvents: hasPermission('events', 'create'),
    canEditEvents: hasPermission('events', 'edit'),
    canDeleteEvents: hasPermission('events', 'delete'),
    // User role status
    isSuperAdmin,
    hasRolePermissions,
    
    // Basic permission checks
    canView,
    canAdd,
    canEdit,
    canDelete,
    canExport,
    canAssign,
    
    // Specific permission checks
    canManageUsers,
    canManageNurseries,
    canManageCameras,
    canManageChildren,
    canManageTeachers,
    canManageParents,
    canManageClassrooms,
    canManageSchedules,
    canManageEdgeDevices,
    canViewReports,
    canExportReports,
    canValidateFaces,
    canViewDashboard,
    canManageVacations,
    canManageHolidays,
    canManagePolicies,
    canManageBilling,
    canViewBilling,
    canManageSubscriptions,
    canManageConsent,
    
    // Settings page specific permissions
    canViewSettings,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canViewUsers,
    canViewRoles,
    canAssignUsers,
    canViewNurseries,
    canCreateNurseries,
    canAccessUserManagement,
    canAccessNurseryManagement,
    canAccessHierarchy,
    canViewSnapshots,
    canDeleteSnapshots,
    canAccessSnapshotSettings,
    canEditSnapshotSettings,
    canAnalyzeSnapshots,
    canAccessChildImageDisplaySettings,
    canEditChildImageDisplaySettings,
    canViewCustomReports,
    canCreateCustomReports,
    canExportCustomReports,
    canShareCustomReports,
    canManageCustomReports,
    canManageRoles,
    canAddRoles,
    canEditRoles,
    canDeleteRoles,
    canViewRolesList,
    canAccessSettings,
    
    // Scoping
    getScope,
    
    // Raw permissions object
    permissions: userRolePermissions,
  };
};
